'use strict';
import isNumber from 'lodash/isNumber';
import {getDomainLink} from '../../../core-utilities/domain/ReduxUtils';
import {truncateName} from '../../../core-utilities/upload-service/FileUtilities';
import {FILE_ASSET_COLLECTION} from '../config';


export const COLLECTION = FILE_ASSET_COLLECTION;
export const CLASS_NAME = 'FileAssetDomain';

export function FileAssetDomainProvider() {
  return {
    $get: [
      '$injector',
      '$filter',
      'AbstractDomainClass',
      'OrgIdMixin',
      'FeedableMixin',
      'restUtils',
      function (
        $injector,
        $filter,
        AbstractDomainClass,
        OrgIdMixin,
        FeedableMixin,
        restUtils,
      ) {

        function FileAssetDomain(data) {
          AbstractDomainClass.call(this, FileAssetDomain, data);
        }

        AbstractDomainClass.__inheritFrom(FileAssetDomain, CLASS_NAME, COLLECTION);
        OrgIdMixin(FileAssetDomain);
        FeedableMixin(FileAssetDomain);

        /*
         * isArchived is required for the functionality of the ArchiveMixin used by FileFulfillmentDomain
         *
         * status:archived allows the file to be archived but not replaced
         *
         * suggested that users simply reject something and later replace the rejected file.
         * this way, the status will remain rejected and the asset can be archived normally.
         */

        FileAssetDomain.$constraints.addConstraints({
          fulfillment: {type: 'DomainLink', domainClass: 'FileFulfillmentDomain', nullable: true},
          file: {type: 'Domain', domainClass: 'FileDomain', nullable: true, immutable: true},
          status: {type: 'string', inList: ['submitted', 'approved', 'rejected', 'archived'], nullable: true},

          isArchived: {type: 'boolean', defaultValue: false}, /* Required by the Archivable-mixin */

          tsk: {type: 'string', nullable: true}, /* Optional parent task id */
          aTk: {type: 'string', nullable: true}, /* Optional parent asset task id */
          org: {type: 'string'}, // Organization ID
          createdBy: {type: 'DomainLink', domainClass: 'UserDomain'},
          updatedBy: {type: 'DomainLink', domainClass: 'UserDomain', nullable: true}, // TODO: update sec rules to enforce this
          dateCreated: {type: 'Date'},
          lastUpdated: {type: 'Date'},

          conversation: {type: 'LinkedDocument', domainClass: 'ConversationDomain', link: 'target'}
        });

        // for fallback API methods
        FileAssetDomain.fallbackIdProperty = 'fileAssetId';
        FileAssetDomain.updateStatusFallbackMethod = 'requirements/files/update-file-asset-status';

        //prototype methods go here

        const updateStatusImpl = restUtils.updateStatus(FileAssetDomain);
        FileAssetDomain.updateStatus = function updateStatusStatic(instance, status) {
          if (instance.status === status && instance.status && instance.status !== 'submitted' && instance.status !== 'archived' && !instance.isArchived) {
            return updateStatusImpl(instance, 'submitted', null); // toggle off for current items
          } else if (instance.status === status && instance.status === 'submitted') {
            return Promise.reject({code: 'requirements.status.change.error.samestatus.message', rejectedValue: status});
          } else {
            return updateStatusImpl(instance, status, null);
          }
        };
        FileAssetDomain.prototype.updateStatus = function updateStatusProto(status) {
          return FileAssetDomain.updateStatus(this, status);
        };

        FileAssetDomain.prototype.setFileDomain = function setFileDomain(fileDomain) {
          const FileDomain = $injector.get('FileDomain');
          let id;

          if (this.file) {
            return Promise.reject('FileAssetDomain.setFileDomain attempting to update immutable field \'file\'');
          } else if (fileDomain) {
            try {
              id = FileDomain.getFileId(fileDomain);

              this.$setLink('file', {id: id, collection: FileDomain.COLLECTION});

              return this.$save();
            } catch (e) {
              return Promise.reject(e);
            }
          } else {
            return Promise.reject('FileAssetDomain.setFileDomain missing required argument fileDomain');
          }
        };

        FileAssetDomain.prototype.toString = function () {
          const date = $filter('date')(this.dateCreated, 'short', 'UTC');
          if (this.$metadata && this.$metadata.requirement && this.$metadata.requirement.name && this.$metadata.fulfillment && isNumber(this.$metadata.fulfillment.ord)) {
            return truncateName(this.$metadata.requirement.name, 12) + ' ' + this.$metadata.fulfillment.ord;
          } else if (!this.$metadata) {
            _setMetadata.call(this);
            return date;
          } else {
            return date;
          }
        };

        function _setMetadata() {
          if (!this.$metadata) {
            Object.defineProperty(this, '$metadata', {configurable: true, value: {}});
            _retrieveMetadata.call(this).then(data => {
              this.$metadata.fulfillment = data.fulfillment;
              this.$metadata.requirement = data.requirement;
            });
          }
        }

        FileAssetDomain.prototype.toStringAsync = function () {
          if (this.$metadata && this.$metadata.requirement && this.$metadata.requirement.name && this.$metadata.fulfillment && isNumber(this.$metadata.fulfillment.ord)) {
            return Promise.resolve(truncateName(this.$metadata.requirement.name, 12) + ' ' + this.$metadata.fulfillment.ord);
          } else {
            return _retrieveMetadata.call(this).then(_toStringFromMetadata.bind(this));
          }
        };

        function _toStringFromMetadata(metadata) {
          if (metadata && metadata.requirement && metadata.requirement.name && metadata.fulfillment && isNumber(metadata.fulfillment.ord)) {
            return `${truncateName(metadata.requirement.name, 12)} ${metadata.fulfillment.ord}`;
          } else {
            return $filter('date')(this.dateCreated, 'short', 'UTC');
          }
        }

        function _retrieveMetadata() {
          return getDomainLink(this, 'fulfillment')
            .then(fulfillment =>
              Promise.props({
                fulfillment: Promise.resolve(fulfillment).catch(() => null),
                requirement: getDomainLink(fulfillment, 'requirement').catch(() => null),
              })
            )
        }

        return FileAssetDomain;
      }]
  };
}



// WEBPACK FOOTER //
// ./src/js/requirements/domain/files/FileAssetDomain.js